import type { LegalEntity } from '@carvia/ros-client-types';
import { BookingLocationType } from '@/entities/bookings/booking-location-type.enum';
import type { BookingLocation } from '@/entities/bookings/booking.entity';
import { calculateRentalDays } from '@/utils/calculate-rental-days';
import { isEuCountryCode } from '@/utils/eu-countries';
import { normalizeVatId } from '@/utils/vat-id';

/** Home country of CarVia GmbH, the fallback where no legal entity is known. */
export const INVOICE_ISSUER_COUNTRY = 'DE';

/** Legal entity renting out the car; without OSS registration it taxes in its home country. */
export type TaxLegalEntity = Pick<LegalEntity, 'countryCode' | 'isOssRegistered'>;

/** Assumed while the legal entity is unknown or loading, like a new legal entity. */
export const DEFAULT_TAX_LEGAL_ENTITY: TaxLegalEntity = {
  countryCode: INVOICE_ISSUER_COUNTRY,
  isOssRegistered: false,
};

/** Rentals longer than this many days are taxed where the customer lives, not at handover. */
export const LONG_RENTAL_DAYS_THRESHOLD = 30;

export type BookingHandoverSource = {
  pickupLocationType?: string | null;
  pickupStation?: { countryCode?: string | null } | null;
  pickupLocationGeocodedAddress?: { country?: string | null } | null;
};

export function normalizeCountryCode(code?: string | null): string | undefined {
  const normalized = code?.trim().toUpperCase();
  return normalized && normalized.length === 2 ? normalized : undefined;
}

export function resolveHandoverCountry(params: BookingHandoverSource): string | undefined {
  if (params.pickupLocationType === BookingLocationType.STREET) {
    return normalizeCountryCode(params.pickupLocationGeocodedAddress?.country);
  }
  return normalizeCountryCode(params.pickupStation?.countryCode);
}

export function resolveHandoverCountryFromLocation(
  location?: BookingLocation | null,
): string | undefined {
  if (!location) {
    return undefined;
  }
  if (location.locationType === BookingLocationType.STREET) {
    return normalizeCountryCode(location.location?.address?.country);
  }
  return normalizeCountryCode(location.station?.countryCode);
}

export function isLongRental(startDate: Date | string, endDate: Date | string): boolean {
  return calculateRentalDays(new Date(startDate), new Date(endDate)) > LONG_RENTAL_DAYS_THRESHOLD;
}

/** Long rentals are taxed where the customer lives; others (and long ones without a customer country yet) at handover. */
export function resolvePlaceOfSupply(params: {
  startDate: Date | string;
  endDate: Date | string;
  handoverCountry?: string | null;
  customerCountry?: string | null;
}): string | undefined {
  const customerCountry = normalizeCountryCode(params.customerCountry);
  return isLongRental(params.startDate, params.endDate) && customerCountry
    ? customerCountry
    : normalizeCountryCode(params.handoverCountry);
}

export function resolveTaxCountry(params: {
  startDate: Date | string;
  endDate: Date | string;
  handoverCountry?: string | null;
  customerCountry?: string | null;
  vatId?: string | null;
  /** Countries with a VAT rate in the yield settings for the rental start. */
  vatRateCountries: readonly string[];
  legalEntity: TaxLegalEntity;
}): string {
  const homeCountry = params.legalEntity.countryCode;
  if (!params.legalEntity.isOssRegistered) {
    return homeCountry;
  }
  const placeOfSupply = resolvePlaceOfSupply(params);
  // Private customers are taxed in the legal entity's home country, as before country taxes, when
  // their place of supply is outside the EU (TEMPORARY, see the decision tree) or has no VAT rate.
  if (
    placeOfSupply &&
    !normalizeVatId(params.vatId) &&
    (!isEuCountryCode(placeOfSupply) || !params.vatRateCountries.includes(placeOfSupply))
  ) {
    return homeCountry;
  }
  return placeOfSupply ?? homeCountry;
}
